import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { captureMutationError } from '@/lib/sentry';
import {
  CreateFeedbackRequest,
  FeedbackStatusValue,
  FeedbackListItem,
  PaginatedResponse,
} from '@property/shared';
import { queryKeys } from '@/lib/query-keys';
import { toast, showMutationErrorToast } from '@/lib/toast';

export function useSubmitFeedback() {
  return useMutation({
    mutationFn: (request: CreateFeedbackRequest) => api.submitFeedback(request),
    onSuccess: () => {
      toast.success('Thanks for your feedback!');
    },
  });
}

export function useUpdateFeedbackStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: FeedbackStatusValue }) =>
      api.updateFeedbackStatus(id, status),

    onMutate: async ({ id, status }) => {
      await qc.cancelQueries({ queryKey: queryKeys.feedback.all() });
      const snapshot = qc.getQueriesData<PaginatedResponse<FeedbackListItem>>({
        queryKey: queryKeys.feedback.all(),
      });
      // Update status in-place so item stays at its current position
      qc.setQueriesData<PaginatedResponse<FeedbackListItem>>(
        { queryKey: queryKeys.feedback.all() },
        (old) =>
          old && Array.isArray(old.data)
            ? {
                ...old,
                data: old.data.map((item) => (item.id === id ? { ...item, status } : item)),
              }
            : old,
      );
      return { snapshot };
    },

    onError: (_err, { id, status }, context) => {
      captureMutationError(_err, { mutation: 'useUpdateFeedbackStatus', feedbackId: id, status });
      context?.snapshot.forEach(([key, val]) => qc.setQueryData(key, val));
      showMutationErrorToast(_err, 'Failed to update status.');
    },

    onSettled: () => {
      qc.invalidateQueries({ queryKey: queryKeys.feedback.all() });
    },
  });
}
